import { Button } from "@components/Button";
import { ButtonSize } from "@components/Button/types";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import {
  useDebounce,
  useErrorBanner,
  useModal,
  useRedeemPromoCode,
  useTheme,
  useVerifyPromotionCode,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { useState } from "react";
import { InputErrorIcon, InputValidIcon } from "./icons";
import { RedeemCodeSuccessModal } from "./RedeemCodeSuccessModal";
import { StyledInput, useStyles } from "./styles";

export const RedeemCodeRoute = () => {
  const styles = useStyles();
  const { theme } = useTheme();
  const { show } = useModal();

  const [value, setValue] = useState("");

  const [verifyPromotionCode, { data, errorMessage }] = useVerifyPromotionCode();
  const [redeemCode, { loading }] = useRedeemPromoCode();

  const debouncedVerifyPromotionCode = useDebounce(verifyPromotionCode);

  const { showErrorBanner } = useErrorBanner();

  const isValid = Boolean(data?.verifyPromotionCode?.active);
  const isInvalid = !isValid && value.length > 0;

  const redemptionText = data?.verifyPromotionCode?.redemptionText ?? "";

  const statusText = (() => {
    if (isValid) return redemptionText;
    if (isInvalid) return errorMessage;
    return "";
  })();

  const statusColor = (() => {
    if (isValid) return theme.promo.valid.default;
    if (isInvalid) return theme.promo.invalid.text;
    return undefined;
  })();

  const icon = (() => {
    if (isValid) return <InputValidIcon />;
    if (isInvalid) return <InputErrorIcon />;
    return null;
  })();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue(value);
    debouncedVerifyPromotionCode({ variables: { code: value } });
  };

  const handleSubmit = async () => {
    try {
      const { data } = await redeemCode({
        variables: {
          code: value,
        },
      });

      const amountOff = data?.redeemPromotionCode?.amountOff;

      if (amountOff != null) {
        show(<RedeemCodeSuccessModal amount={amountOff} />, false);
      } else {
        showErrorBanner();
      }
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    }
  };

  const props: MainLayoutProps = {
    pageHeader: i18n.t(Translations.RedeemCodeRouteHeader),
  };

  return (
    <MainLayout {...props}>
      <Spacing size={SpacingSizes.XL} />

      <Body style={styles.description}>{i18n.t(Translations.RedeemCodeRouteDescription)}</Body>
      <Spacing size={SpacingSizes.XL} />

      <div style={styles.inputContainer}>
        <StyledInput
          type="text"
          style={styles.input}
          placeholder={i18n.t(Translations.RedeemCodeInputPlaceholder)}
          onChange={onChange}
          value={value}
        />
        <span style={styles.icon}>{icon}</span>
      </div>
      <Spacing size={SpacingSizes.XL} />

      {statusText ? (
        <Body style={{ ...styles.statusText, ...{ color: statusColor } }}>{statusText}</Body>
      ) : null}
      <Spacing size={SpacingSizes.XL} />

      <Button
        title={i18n.t(Translations.RedeemCodeRouteRedeemButton)}
        size={ButtonSize.Small}
        buttonStyle={styles.button}
        loading={loading}
        disabled={!isValid}
        onClick={handleSubmit}
      />
    </MainLayout>
  );
};
